/**
 * "Waiting on the team" (Specs/submission-pack-spec.md P3, Task C2; was "Needs
 * from you" — renamed by Specs/2026-09-23-status-words-and-editor-header-plan.md
 * D1. The component and store names keep the old wording).
 *
 * One list, three sources, merged server-side:
 *  - `todo`    — a `[TODO: …]` note sitting in a draft, answerable here.
 *  - `fact`    — a fact the drafting job wants a person to confirm. Reads
 *                "Confirm: …" and takes the same actions as a todo (answer,
 *                resolve). No "Apply in section": there is no marker to apply
 *                the answer to.
 *  - `question` — a durable question the AI raised (R1 Q1/Q2). Rendered like
 *                a fact: "Question: …", its reason, Answer. No Resolve — a
 *                question is answered (or marked not known in the panel or on
 *                the document overview), never ticked off; the server refuses it.
 *                Links to the document and section it is about, as a todo
 *                does, when the server says where (R1 J-tune18, F06).
 *  - `manual`  — an ask a bid member typed ("confirm the site visit date").
 *
 * The third source — `missing`, a not_ready form/attachment re-framed as an ask
 * — was removed (Specs/2026-08-28-submission-pack-needs-slim.md). Those rows
 * duplicated the pack table sitting directly below, so a pack of 53 unattached
 * items painted 53 "Attach: …" rows that told the user nothing new.
 *
 * The COUNT here is asks, not markers. The blockers band above counts raw
 * `[TODO: …]` tokens, and the two numbers legitimately differ: answering a
 * marker takes it off this list while the token is still sitting in the draft
 * and still heading for the export. The two rows are worded apart on purpose —
 * "unresolved TODO markers in drafts" up there, "Waiting on the team · N" here.
 *
 * Collapsed by default when non-empty (Specs/2026-09-23-submission-pack-redesign-plan.md,
 * Task 3.3): the header row is a disclosure, and the page owns the open state
 * so the "N waiting on the team" chip can open it.
 *
 * Flat by the design system's rules — this is a section of a page, not a card.
 */

import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight, Plus, Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { AddItemModal, type ModalFieldConfig } from '@/components/extraction/AddItemModal'
import { OwnerPicker } from '@/components/extraction/OwnerPicker'
import { getResponseTargetPath, getTodoTargetPath } from '@/lib/response-linking'
import type { BidTodo, NeedsFromYouEntry, PackAsk, PackQuestionLocation } from '@/lib/api'
import { PACK_QUESTION_PREFIX, QUESTION_NOT_KNOWN } from '@/lib/question-copy'

/**
 * The kinds this component knows how to draw. Written as an `Extract` so it
 * survives the API type gaining or losing an arm — the filter below stays a
 * real narrowing either way.
 */
type RenderableEntry = Extract<NeedsFromYouEntry, { kind: 'todo' | 'fact' | 'question' | 'manual' }>

function isRenderable(entry: NeedsFromYouEntry): entry is RenderableEntry {
  return entry.kind === 'todo' || entry.kind === 'fact' || entry.kind === 'question' || entry.kind === 'manual'
}

/** The section's anchor — the "N waiting on the team" chip scrolls here. */
export const NEEDS_FROM_YOU_ANCHOR = 'needs-from-you'

interface TeamMember {
  id: string
  userId: string
  fullName: string | null
  avatarUrl: string | null
}

export interface NeedsFromYouProps {
  bidId: string
  entries: NeedsFromYouEntry[]
  teamMembers?: TeamMember[]
  readOnly?: boolean
  /** Whether the list is open. Controlled by the page. */
  isExpanded: boolean
  onExpandedChange: (isExpanded: boolean) => void
  /** Save an answer. */
  onAnswer: (entry: NeedsFromYouEntry, answer: string) => void
  onResolve: (entry: NeedsFromYouEntry) => void
  onSetOwner: (entry: NeedsFromYouEntry, ownerId: string | null) => void
  onAddAsk: (text: string, ownerId: string | null) => void
  /** Opens the section editor with the AI panel seeded. Answered todos only. */
  onApplyInSection: (todo: BidTodo, answer: string) => void
}

const ADD_ASK_FIELDS: ModalFieldConfig[] = [
  { key: 'text', label: 'Ask', type: 'textarea', required: true, fullWidth: true },
  { key: 'ownerId', label: 'Owner', type: 'owner' },
]

/**
 * A marker has no id, so its React key is where it is plus what it says — the
 * same identity the server keys its overlay on.
 */
function entryKey(entry: RenderableEntry): string {
  if (entry.kind === 'todo') {
    return `todo:${entry.todo.artifactId}:${entry.todo.sectionId}:${entry.todo.text}`
  }
  return `${entry.kind}:${entry.ask.id}`
}

/**
 * One unique key per entry. The server dedupes identical markers into one
 * entry, but a backend from before that change (the deploy gap) still sends
 * them one per marker — and duplicate keys make React drop or merge rows.
 * A repeat gets a positional suffix so every row still renders.
 */
function uniqueEntryKeys(entries: RenderableEntry[]): string[] {
  const seen = new Map<string, number>()
  return entries.map((entry) => {
    const base = entryKey(entry)
    const count = seen.get(base) ?? 0
    seen.set(base, count + 1)
    return count === 0 ? base : `${base}#${count}`
  })
}

function askOf(entry: RenderableEntry): PackAsk | null {
  if (entry.kind === 'todo') return entry.overlay
  return entry.ask
}

export function NeedsFromYou({
  bidId,
  entries,
  teamMembers = [],
  readOnly = false,
  isExpanded,
  onExpandedChange,
  onAnswer,
  onResolve,
  onSetOwner,
  onAddAsk,
  onApplyInSection,
}: NeedsFromYouProps) {
  const [isAdding, setIsAdding] = useState(false)

  // A stale backend (deploy gap) or a future kind must render as absent, not
  // as unstyled mystery rows — filter to what this component knows how to draw.
  const visible = entries.filter(isRenderable)
  const keys = uniqueEntryKeys(visible)
  const hasEntries = visible.length > 0

  const addButton = !readOnly && (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 px-2 text-xs"
      onClick={() => setIsAdding(true)}
    >
      <Plus className="h-4 w-4 mr-1" />
      Add ask
    </Button>
  )

  const modal = (
    <AddItemModal
      open={isAdding}
      onOpenChange={(open) => {
        if (!open) setIsAdding(false)
      }}
      title="Add ask"
      description="Something only a person can answer — a price, a date, a named team member."
      fields={ADD_ASK_FIELDS}
      teamMembers={teamMembers}
      submitLabel="Add"
      onSubmit={(values) => {
        const raw = values['text']
        const text = typeof raw === 'string' ? raw.trim() : ''
        if (!text) return
        const rawOwner = values['ownerId']
        const ownerId = typeof rawOwner === 'string' && rawOwner !== '' ? rawOwner : null
        onAddAsk(text, ownerId)
        setIsAdding(false)
        // Show the list the new ask is about to land in.
        onExpandedChange(true)
      }}
    />
  )

  const listId = `${NEEDS_FROM_YOU_ANCHOR}-list`
  const Chevron = isExpanded ? ChevronDown : ChevronRight

  return (
    // scroll-mt clears the sticky pack header when the chip scrolls here.
    <section id={NEEDS_FROM_YOU_ANCHOR} aria-label="Waiting on the team" className="scroll-mt-20">
      <div className="flex items-center justify-between gap-2 pb-1">
        <h3 className="text-sm font-medium text-foreground">
          {hasEntries ? (
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-controls={listId}
              onClick={() => onExpandedChange(!isExpanded)}
              className="-ml-1 inline-flex items-center gap-1 rounded-md px-1 hover:bg-muted"
            >
              <Chevron aria-hidden className="h-4 w-4 shrink-0 text-subtle" />
              Waiting on the team <span className="text-meta">{visible.length}</span>
            </button>
          ) : (
            <>
              Waiting on the team <span className="text-meta">{visible.length}</span>
            </>
          )}
        </h3>
        {addButton}
      </div>

      {/* Collapsed to the count line when empty (spec P2.3) or closed — no
          list, no empty-group filler. The heading above already has the count. */}
      {hasEntries && isExpanded && (
        <ul
          id={listId}
          aria-label="Waiting on the team"
          className="divide-y divide-border border-t border-border"
        >
          {visible.map((entry, index) => (
            <NeedRow
              key={keys[index]}
              bidId={bidId}
              entry={entry}
              teamMembers={teamMembers}
              readOnly={readOnly}
              onAnswer={onAnswer}
              onResolve={onResolve}
              onSetOwner={onSetOwner}
              onApplyInSection={onApplyInSection}
            />
          ))}
        </ul>
      )}

      {modal}
    </section>
  )
}

// ===================
// Row
// ===================

interface NeedRowProps {
  bidId: string
  entry: RenderableEntry
  teamMembers: TeamMember[]
  readOnly: boolean
  onAnswer: (entry: NeedsFromYouEntry, answer: string) => void
  onResolve: (entry: NeedsFromYouEntry) => void
  onSetOwner: (entry: NeedsFromYouEntry, ownerId: string | null) => void
  onApplyInSection: (todo: BidTodo, answer: string) => void
}

function NeedRow({
  bidId,
  entry,
  teamMembers,
  readOnly,
  onAnswer,
  onResolve,
  onSetOwner,
  onApplyInSection,
}: NeedRowProps) {
  const ask = askOf(entry)
  const answer = ask?.answer ?? null
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState('')

  const startEditing = useCallback(() => {
    setDraft(answer ?? '')
    setIsEditing(true)
  }, [answer])

  const save = useCallback(() => {
    const trimmed = draft.trim()
    if (!trimmed) return
    onAnswer(entry, trimmed)
    setIsEditing(false)
  }, [draft, entry, onAnswer])

  const text = entry.kind === 'todo' ? entry.todo.text : entry.ask.text
  // Todos, facts and the AI's questions are answered; a manual ask is owned instead.
  const isAnswerable = entry.kind === 'todo' || entry.kind === 'fact' || entry.kind === 'question'
  const questionDetails = entry.kind === 'question' ? entry.ask.question : undefined

  return (
    <li className="flex flex-col gap-2 py-2.5">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-foreground">
            {entry.kind === 'fact' && <span className="text-muted-foreground">Confirm: </span>}
            {entry.kind === 'question' && <span className="text-muted-foreground">{PACK_QUESTION_PREFIX}</span>}
            {text}
          </p>
          {questionDetails?.reason && <p className="text-meta">{questionDetails.reason}</p>}
          {questionDetails?.status === 'deferred' && <p className="text-meta">{QUESTION_NOT_KNOWN}</p>}
          {entry.kind === 'question' && entry.location && (
            <QuestionContext bidId={bidId} location={entry.location} />
          )}
          {entry.kind === 'todo' && (
            <TodoContext bidId={bidId} todo={entry.todo} occurrences={entry.occurrences} />
          )}
          {answer && !isEditing && (
            <p className="text-meta mt-1 whitespace-pre-wrap">{answer}</p>
          )}
        </div>

        {entry.kind === 'manual' && (
          <div className="w-28 shrink-0">
            <OwnerPicker
              value={entry.ask.ownerId}
              teamMembers={teamMembers}
              disabled={readOnly}
              onChange={(userId) => onSetOwner(entry, userId)}
            />
          </div>
        )}

        {!readOnly && (
          <div className="flex shrink-0 items-center gap-1">
            {isAnswerable && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={startEditing}
              >
                {answer ? 'Edit answer' : 'Answer'}
              </Button>
            )}
            {entry.kind === 'todo' && answer && (
              <Button
                variant="ai-ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onApplyInSection(entry.todo, answer)}
              >
                <Wand2 className="h-4 w-4 mr-1" />
                Apply in section
                <ChevronRight className="ml-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              </Button>
            )}
            {entry.kind !== 'question' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onResolve(entry)}
              >
                Resolve
              </Button>
            )}
          </div>
        )}
      </div>

      {isEditing && (
        <div className="flex flex-col gap-2">
          <Textarea
            aria-label={`Answer: ${text}`}
            value={draft}
            rows={3}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-7 px-3 text-xs" onClick={save}>
              Save answer
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </li>
  )
}

/**
 * Where an AI question's answer goes (R1 J-tune18, F06): the same link a todo
 * row carries, routed by the document's editor family; the section when the
 * server resolved one, else the document.
 */
function QuestionContext({ bidId, location }: { bidId: string; location: PackQuestionLocation }) {
  const target = { id: location.artifactId, artifactType: location.artifactType, scopeKind: location.scopeKind }
  return (
    <p className="text-meta truncate">
      <Link
        to={getResponseTargetPath(target, bidId, location.sectionKey ? encodeURIComponent(location.sectionKey) : null)}
        className="text-[hsl(var(--link))] hover:underline"
      >
        {location.artifactName}
        {location.sectionTitle ? ` · ${location.sectionTitle}` : ''}
      </Link>
    </p>
  )
}

/**
 * Where the marker lives. The route is resolved by the artifact's editor family
 * (`getTodoTargetPath`) — the scan covers questionnaires and document-scope
 * artifacts too, so a hardcoded prose path would send those to the wrong editor.
 */
function TodoContext({
  bidId,
  todo,
  occurrences,
}: {
  bidId: string
  todo: BidTodo
  /** Identical markers folded into this one question. Absent on an old backend. */
  occurrences: number | undefined
}) {
  return (
    <p className="text-meta truncate">
      <Link
        to={getTodoTargetPath(todo, bidId)}
        className="text-[hsl(var(--link))] hover:underline"
      >
        {todo.artifactName}
        {todo.sectionTitle ? ` · ${todo.sectionTitle}` : ''}
      </Link>
      {occurrences !== undefined && occurrences > 1 && ` · in ${occurrences} places`}
    </p>
  )
}
